import React from 'react';
import { css } from 'styled-components';
import { borders, colors, typography } from '../../settings';
import {
    FormDescription,
    FormDescriptionProps,
    FormError,
    FormErrorProps,
    FormHelperText,
    FormHelperTextProps,
    FormLabel,
    FormLabelProps,
} from '../Form';
import { Stack } from '../Stack';
import { getFormIDs } from './getFormIDs';

export const FieldFocusStyling = css`
    border-color: ${colors.fiftyFirstShade};
    outline: none;
`;

export const FieldPlaceholderStyling = css<{ disabled?: boolean }>`
    color: ${({ disabled }) => (disabled ? colors.pebbleBeach : colors.fiftyFirstShade)};
    opacity: 1;
`;

export const FieldStyling = css<{ disabled?: boolean; error?: boolean }>`
    border: ${borders.textInput};
    border-radius: ${borders.borderRadiuses.medium}px;
    color: ${colors.puntRoad};
    line-height: 1.5;
    font-size: ${typography.typeScale.size3}px;
    padding: 8px 12px;
    width: 100%;

    ${({ disabled }) =>
        disabled &&
        css`
            background-color: ${colors.karlTheFog};
            border-color: ${colors.t1000};
            color: ${colors.pebbleBeach};
        `}

    ${({ error }) =>
        error &&
        css`
            border-color: ${colors.semantic.error};
        `}
`;

export interface FieldProps {
    /**
     * Adds additional context above the field
     */
    description?: FormDescriptionProps['children'];
    disabled?: boolean;
    /**
     * Adds error styling to the input when truthy, and displays a message underneath the input.
     * The focus outline will take priority over this styling. The error state (and string) should
     * be removed when the data inside the input is changed.
     */
    error?: FormErrorProps['children'];
    /**
     * Adds additional context below the field group. Not shown when an error is present
     */
    helperText?: FormHelperTextProps['children'];
    id: string;
    /**
     * Primary label linked to the input. In most cases a string can be used for the default styling,
     * or a ReactNode can be passed instead.
     * If a string is passed, it will be rendered inside of a `<label>`
     */
    label: FormLabelProps['children'];
    required?: boolean;
}

interface PrivateFieldProps extends FieldProps {
    children: React.ReactNode;
}

export function Field({
    children,
    description,
    disabled,
    error,
    helperText,
    id,
    label,
    required,
}: PrivateFieldProps) {
    const {
        ariaDescribedBy,
        descriptionId,
        errorId,
        helperTextId,
        labelId,
        showHelperText,
    } = getFormIDs({
        description,
        error,
        helperText,
        id,
    });

    return (
        <Stack space="space6">
            {typeof label === 'string' ? (
                <FormLabel id={labelId} htmlFor={id} required={required} disabled={disabled}>
                    {label}
                </FormLabel>
            ) : (
                label
            )}

            <Stack space="space5">
                {!!description && (
                    <FormDescription id={descriptionId} disabled={disabled}>
                        {description}
                    </FormDescription>
                )}

                {React.Children.map(children, (child) => {
                    if (React.isValidElement(child)) {
                        return React.cloneElement(child, { 'aria-describedby': ariaDescribedBy });
                    }
                })}

                {!!error && <FormError id={errorId}>{error}</FormError>}

                {showHelperText && (
                    <FormHelperText id={helperTextId} disabled={disabled}>
                        {helperText}
                    </FormHelperText>
                )}
            </Stack>
        </Stack>
    );
}
